import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Space, List, Avatar, Badge, FloatButton, Card } from 'antd';
import { SendOutlined, UserOutlined, RobotOutlined, ClearOutlined, CommentOutlined, CloseOutlined } from '@ant-design/icons';
import { SendIcon } from 'src/assets/icons';
import { useLocation } from 'react-router-dom';
import { sendWebChatMessageWithHistory, ChatHistoryMessage } from 'src/services/gpt-service';
import { getSocket } from 'src/configs/socket-config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AIAssistantSidebar.scss';

const { TextArea } = Input;

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

interface PageContext {
  pageName: string;
  contextInfo: string;
}

interface AIAssistantSidebarProps {
  className?: string;
  isOpen: boolean;
  onToggle: () => void;
}

const AIAssistantSidebar: React.FC<AIAssistantSidebarProps> = ({ className, isOpen, onToggle }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! I am your AI assistant. How can I help you today?',
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef(getSocket());
  const location = useLocation();

  // Get current page context
  const getPageContext = (): PageContext => {
    const path = location.pathname;
    
    if (path.includes('/clients')) {
      return {
        pageName: 'Clients Management',
        contextInfo: 'I can help you with client-related questions, documentation, treatment planning, and client communication strategies.'
      };
    } else if (path.includes('/homework')) {
      return {
        pageName: 'Homework & Assignments',
        contextInfo: 'I can assist with homework assignment creation, patient progress tracking, and therapeutic exercise recommendations.'
      };
    } else if (path.includes('/messages')) {
      return {
        pageName: 'Messages',
        contextInfo: 'I can help with professional communication, message drafting, and clinical correspondence guidelines.'
      };
    } else if (path.includes('/files')) {
      return {
        pageName: 'Files Management',
        contextInfo: 'I can assist with file organization, documentation standards, and clinical record keeping practices.'
      };
    } else if (path.includes('/psychoeducation')) {
      return {
        pageName: 'Psychoeducation',
        contextInfo: 'I can help create educational content, explain therapeutic concepts, and develop patient education materials.'
      };
    } else if (path.includes('/practitioners')) {
      return {
        pageName: 'Practitioners',
        contextInfo: 'I can assist with practitioner management, team coordination, and professional development resources.'
      };
    } else if (path.includes('/templates')) {
      return {
        pageName: 'Templates',
        contextInfo: 'I can help create clinical templates, standardize documentation, and optimize workflow templates.'
      };
    } else if (path.includes('/live-transcribe')) {
      return {
        pageName: 'Live Transcription',
        contextInfo: 'I can assist with session documentation, transcription review, and clinical note organization.'
      };
    } else if (path === '/' || path.includes('/home')) {
      return {
        pageName: 'Dashboard',
        contextInfo: 'I can help with practice overview, daily task management, and general clinical questions.'
      };
    }
    
    return {
      pageName: 'General',
      contextInfo: 'I can assist with general clinical questions, practice management, and therapeutic guidance.'
    };
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Setup socket connection
  useEffect(() => {
    const socket = socketRef.current;
    
    socket.connect();
    
    socket.joinWebChat((message) => {
      if (streamingMessageId) {
        setMessages(prevMessages => {
          return prevMessages.map(msg => {
            if (msg.id === streamingMessageId) {
              return { ...msg, text: message.content };
            }
            return msg;
          });
        });
      }
    });

    return () => {
      socket.leaveWebChat();
      socket.disconnect();
    };
  }, [streamingMessageId]);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Update context message when page changes
  useEffect(() => {
    const context = getPageContext();
    const contextMessage: Message = {
      id: `context-${Date.now()}`,
      text: `I'm now ready to help you with ${context.pageName}. ${context.contextInfo}`,
      sender: 'ai',
      timestamp: new Date(),
    };
    
    // Only add context message if we're navigating to a new page and it's different
    setMessages(prev => {
      // Don't add context message on initial load or if it's the same as last message
      if (prev.length <= 1) return prev;
      
      const lastMessage = prev[prev.length - 1];
      if (lastMessage?.text !== contextMessage.text && !lastMessage?.text.includes(context.pageName)) {
        return [...prev, contextMessage];
      }
      return prev;
    });
  }, [location.pathname]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleToggle = () => {
    onToggle();
    if (!isOpen) {
      setUnreadCount(0);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const context = getPageContext();
    const contextualInput = `Context: I'm currently on the ${context.pageName} page. ${inputValue}`;

    const newUserMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages([...messages, newUserMessage]);
    setInputValue('');
    setIsLoading(true);

    // Create a placeholder for the AI response with streaming
    const streamingId = (Date.now() + 1).toString();
    const streamingMessage: Message = {
      id: streamingId,
      text: '',
      sender: 'ai',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, streamingMessage]);
    setStreamingMessageId(streamingId);

    try {
      // Convert the local message format to the API's expected format
      const messageHistory: ChatHistoryMessage[] = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      // Call API with contextual information
      await sendWebChatMessageWithHistory(contextualInput, messageHistory);
      
      setStreamingMessageId(null);
      
      // If sidebar is closed, increment unread count
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => {
        return prev.map(msg => {
          if (msg.id === streamingId) {
            return {
              ...msg,
              text: 'I apologize, but I encountered an error. Please try again later.'
            };
          }
          return msg;
        });
      });
      setStreamingMessageId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    const context = getPageContext();
    setMessages([
      {
        id: Date.now().toString(),
        text: `Hello! I am your AI assistant. I'm ready to help you with ${context.pageName}. ${context.contextInfo}`,
        sender: 'ai',
        timestamp: new Date(),
      }
    ]);
  };

  const context = getPageContext();

  // Check if chat bubble should be shown (configurable via .env)
  // Defaults to false (hidden) if not set or if set to anything other than 'true'
  const showChatBubble = process.env.REACT_APP_SHOW_AI_CHAT_BUBBLE?.toLowerCase() === 'true';

  return (
    <>
      {/* Floating Action Button - only show when sidebar is closed and when enabled via env */}
      {!isOpen && showChatBubble && (
        <FloatButton
          icon={<Badge count={unreadCount} size="small"><CommentOutlined /></Badge>}
          onClick={handleToggle}
          className={`ai-assistant-fab ${className || ''}`}
          style={{ 
            right: isMobile ? 16 : 24,
            bottom: isMobile ? 16 : 24,
          }}
        />
      )}

      {/* Sticky Sidebar */}
      <div className={`ai-assistant-sidebar ${isOpen ? 'open' : 'closed'} ${className || ''}`}>
        <Card 
          className="ai-assistant-card"
          title={
            <div className="ai-assistant-header">
              <div className="ai-assistant-title">
                <RobotOutlined style={{ color: '#1976d2', marginRight: 8 }} />
                AI Assistant
              </div>
              <div className="ai-assistant-context">
                {context.pageName}
              </div>
            </div>
          }
          extra={
            <Space>
              <Button
                icon={<ClearOutlined />}
                onClick={clearChat}
                type="text"
                size="small"
              >
                Clear
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleToggle}
                type="text"
                size="small"
              />
            </Space>
          }
        >
          <div className="ai-assistant-content">
            <div className="messages-container">
              <List
                itemLayout="horizontal"
                dataSource={messages}
                renderItem={(message) => (
                  <List.Item className={`message-item ${message.sender}`}>
                    <List.Item.Meta
                      avatar={
                        message.sender === 'user' ? 
                          <Avatar icon={<UserOutlined />} size="small" /> : 
                          <Avatar icon={<RobotOutlined />} style={{ backgroundColor: '#1976d2' }} size="small" />
                      }
                      description={
                        <div className="message-content">
                          <div className="message-text">
                            {message.sender === 'ai' ? (
                              <ReactMarkdown 
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  // Customize component rendering if needed
                                  p: ({ children }) => <p style={{ margin: '0 0 8px 0', lineHeight: '1.4' }}>{children}</p>,
                                  ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '16px' }}>{children}</ul>,
                                  ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: '16px' }}>{children}</ol>,
                                  li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
                                  code: ({ children, className }) => {
                                    const isInline = !className;
                                    return isInline ? (
                                      <code style={{ 
                                        backgroundColor: 'rgba(0, 0, 0, 0.06)', 
                                        padding: '2px 4px', 
                                        borderRadius: '3px',
                                        fontSize: '0.9em'
                                      }}>
                                        {children}
                                      </code>
                                    ) : (
                                      <pre style={{ 
                                        backgroundColor: 'rgba(0, 0, 0, 0.06)', 
                                        padding: '8px 12px', 
                                        borderRadius: '6px',
                                        overflow: 'auto',
                                        fontSize: '0.9em',
                                        margin: '8px 0'
                                      }}>
                                        <code>{children}</code>
                                      </pre>
                                    );
                                  },
                                  blockquote: ({ children }) => (
                                    <blockquote style={{ 
                                      borderLeft: '3px solid #ddd', 
                                      paddingLeft: '12px', 
                                      margin: '8px 0',
                                      fontStyle: 'italic',
                                      color: '#666'
                                    }}>
                                      {children}
                                    </blockquote>
                                  ),
                                  h1: ({ children }) => <h1 style={{ margin: '12px 0 8px 0', fontSize: '1.2em', fontWeight: 'bold' }}>{children}</h1>,
                                  h2: ({ children }) => <h2 style={{ margin: '12px 0 8px 0', fontSize: '1.15em', fontWeight: 'bold' }}>{children}</h2>,
                                  h3: ({ children }) => <h3 style={{ margin: '10px 0 6px 0', fontSize: '1.1em', fontWeight: 'bold' }}>{children}</h3>,
                                  h4: ({ children }) => <h4 style={{ margin: '8px 0 4px 0', fontSize: '1.05em', fontWeight: 'bold' }}>{children}</h4>,
                                  h5: ({ children }) => <h5 style={{ margin: '8px 0 4px 0', fontSize: '1em', fontWeight: 'bold' }}>{children}</h5>,
                                  h6: ({ children }) => <h6 style={{ margin: '8px 0 4px 0', fontSize: '0.95em', fontWeight: 'bold' }}>{children}</h6>,
                                  table: ({ children }) => (
                                    <table style={{ 
                                      borderCollapse: 'collapse', 
                                      width: '100%', 
                                      margin: '8px 0',
                                      fontSize: '0.9em'
                                    }}>
                                      {children}
                                    </table>
                                  ),
                                  th: ({ children }) => (
                                    <th style={{ 
                                      border: '1px solid #ddd', 
                                      padding: '6px 8px', 
                                      backgroundColor: '#f5f5f5',
                                      fontWeight: 'bold'
                                    }}>
                                      {children}
                                    </th>
                                  ),
                                  td: ({ children }) => (
                                    <td style={{ 
                                      border: '1px solid #ddd', 
                                      padding: '6px 8px'
                                    }}>
                                      {children}
                                    </td>
                                  ),
                                  strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
                                  em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                  a: ({ children, href }) => (
                                    <a 
                                      href={href} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{ 
                                        color: '#1976d2', 
                                        textDecoration: 'underline'
                                      }}
                                    >
                                      {children}
                                    </a>
                                  ),
                                  hr: () => <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #ddd' }} />
                                }}
                              >
                                {message.text}
                              </ReactMarkdown>
                            ) : (
                              message.text
                            )}
                          </div>
                          <div className="message-time">
                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
              <div ref={messagesEndRef} />
            </div>
            
            <div className="input-container">
              <div className="ai-assistant-input">
                <TextArea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={`Ask about ${context.pageName.toLowerCase()}...`}
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  className="ai-assistant-textarea"
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
              </div>
              <SendIcon 
                className={`ai-assistant-send-icon ${isLoading ? 'disabled' : ''}`}
                onClick={!isLoading ? handleSendMessage : undefined}
              />
            </div>
          </div>
        </Card>
      </div>
    </>
  );
};

export default AIAssistantSidebar; 